import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { QrCode, RefreshCw, ExternalLink, ScanLine, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

export default function AdminQrAnalytics() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/flyer-studio/qr-links');
      setLinks(r.data.links || []);
    } catch (e) {
      toast.error('Could not load QR links');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalScans = useMemo(() => links.reduce((s, l) => s + (l.scans || 0), 0), [links]);

  // Group by campaign
  const campaigns = useMemo(() => {
    const map = {};
    for (const l of links) {
      const key = (l.campaign || '').trim() || 'Uncategorized';
      if (!map[key]) map[key] = { campaign: key, scans: 0, links: 0, last_scan_at: null };
      map[key].scans += l.scans || 0;
      map[key].links += 1;
      if (l.last_scan_at && (!map[key].last_scan_at || l.last_scan_at > map[key].last_scan_at)) map[key].last_scan_at = l.last_scan_at;
    }
    return Object.values(map).sort((a, b) => b.scans - a.scans);
  }, [links]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto" data-testid="admin-qr-analytics">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-[#1D4ED8] mb-1"><QrCode size={14} /> QR Analytics</div>
            <h1 className="text-2xl font-black text-[#0A0F1C]">QR Scan Dashboard</h1>
            <p className="text-sm text-[#475569] mt-1">Every QR link you've generated, with total scans and the last time each was scanned.</p>
          </div>
          <button type="button" onClick={load} data-testid="qr-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#E2E8F0] text-[#475569] rounded-lg hover:bg-white"><RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh</button>
        </div>

        {/* Summary */}
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid="qr-stat-scans">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Total scans</div>
            <div className="text-3xl font-black text-[#0A0F1C] mt-1">{totalScans}</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid="qr-stat-links">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">QR links</div>
            <div className="text-3xl font-black text-[#0A0F1C] mt-1">{links.length}</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid="qr-stat-campaigns">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#64748B]">Campaigns</div>
            <div className="text-3xl font-black text-[#0A0F1C] mt-1">{campaigns.length}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-[#1D4ED8]" size={26} /></div>
        ) : links.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="qr-empty">
            <QrCode size={36} className="mx-auto mb-3 text-[#CBD5E1]" />
            <p className="text-[#475569] font-medium">No QR links yet.</p>
            <p className="text-sm text-[#94A3B8] mt-1">Generate trackable QR codes in Flyer Studio and they'll show up here.</p>
          </div>
        ) : (
          <>
            {/* By campaign */}
            <h2 className="text-sm font-black uppercase tracking-wider text-[#0A0F1C] mb-2">By campaign</h2>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden mb-8" data-testid="qr-campaigns">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-black uppercase tracking-wider text-[#64748B] bg-[#F8FAFC]">
                    <th className="px-4 py-2.5">Campaign</th>
                    <th className="px-4 py-2.5 text-center">Links</th>
                    <th className="px-4 py-2.5 text-center">Scans</th>
                    <th className="px-4 py-2.5">Last scanned</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.campaign} data-testid={`qr-campaign-row`} className="border-t border-[#F1F5F9]">
                      <td className="px-4 py-2.5 font-bold text-[#0A0F1C]">{c.campaign}</td>
                      <td className="px-4 py-2.5 text-center text-[#475569]">{c.links}</td>
                      <td className="px-4 py-2.5 text-center"><span className="inline-flex items-center gap-1 font-black text-[#1D4ED8]"><ScanLine size={13} /> {c.scans}</span></td>
                      <td className="px-4 py-2.5 text-[#475569]"><span className="inline-flex items-center gap-1"><Clock size={12} className="text-[#94A3B8]" /> {fmtDate(c.last_scan_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* All links */}
            <h2 className="text-sm font-black uppercase tracking-wider text-[#0A0F1C] mb-2">All QR links</h2>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid="qr-links-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-black uppercase tracking-wider text-[#64748B] bg-[#F8FAFC]">
                    <th className="px-4 py-2.5">Campaign</th>
                    <th className="px-4 py-2.5">Destination</th>
                    <th className="px-4 py-2.5 text-center">Scans</th>
                    <th className="px-4 py-2.5">Last scanned</th>
                  </tr>
                </thead>
                <tbody>
                  {links.map((l) => (
                    <tr key={l.code} data-testid="qr-link-row" className="border-t border-[#F1F5F9]">
                      <td className="px-4 py-2.5 font-bold text-[#0A0F1C]">{(l.campaign || '').trim() || <span className="text-[#94A3B8] font-normal">Uncategorized</span>}</td>
                      <td className="px-4 py-2.5 max-w-[280px] truncate">
                        <a href={l.destination_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1D4ED8] hover:underline truncate">{l.destination_url} <ExternalLink size={11} className="shrink-0" /></a>
                      </td>
                      <td className="px-4 py-2.5 text-center"><span className="inline-flex items-center gap-1 font-black text-[#1D4ED8]"><ScanLine size={13} /> {l.scans || 0}</span></td>
                      <td className="px-4 py-2.5 text-[#475569]"><span className="inline-flex items-center gap-1"><Clock size={12} className="text-[#94A3B8]" /> {fmtDate(l.last_scan_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
